import { missingChannelEpisodeFilters } from "@/components/dataset/episodeReviewState";
import { IconButton, Scrollbar } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { episodeReviewDirtyStore, useEpisodeReviewDirty } from "@/components/dataset/annotation";
import { FullscreenWindow, HatTextModal, type WindowEntry } from "@/components/modal";
import type { DatasetReviewOutcome } from "@/components/dataset/review/DatasetReviewPanel";
import { windowStack, windowTaskbarTextLabel } from "@/app/windowStack";
import {
  DatasetDetailActions,
  DatasetDetailBody,
  datasetDetailTitleBadge,
  adjacentDatasetDetailTab,
  datasetDetailTabs,
  datasetDetailModeTab,
  type DatasetDetailTab,
} from "./DatasetDetailContent";
import { DatasetDetailModals } from "./DatasetDetailModals";
import type { DatasetDetailWindowParams } from "@/app/datasetDetailWindow";
import {
  discardGuardedEdits,
  unsavedEditsCaption,
  type DatasetDetailExitGuardTargets,
  type DatasetDetailExitIntent,
} from "./datasetDetailExitGuard";
import { useDatasetDetailController } from "./useDatasetDetailController";
import styles from "./DatasetDetailWindowContent.module.css";

type DatasetDetailWindowContentProps = {
  entry: WindowEntry;
  // 스택에서 이 엔트리가 전체화면으로 보이는지. false 면 이 컴포넌트는 mount 된 채 남는다. 셸은
  // 본문을 렌더하지 않는다.
  active: boolean;
};

// 전체화면 창 스택의 datasetDetail 콘텐츠. 라우트 페이지(DatasetDetailPage)와 같은 controller·
// 본문·서브모달을 재사용한다. 단, PageBase 레이아웃 대신 FullscreenWindow 셸에 담는다.
// 호스트가 entry.id 를 key 로 준다. 그래서 데이터셋마다 controller 가 새로 생긴다. 최소화
// (active=false)해도 이 컴포넌트는 mount 된 채 남는다. 그래서 controller 상태가 보존된다. 셸은
// 본문을 unmount 한다. 본문 자식의 로컬 상태는 남지 않는다.
export function DatasetDetailWindowContent({
  entry,
  active,
}: DatasetDetailWindowContentProps): ReactNode {
  const { datasetUuid, mode, initialTab, annotationSetUuid, episodeFilters } =
    entry.params as DatasetDetailWindowParams;
  const closeSelf = useCallback(() => windowStack.close(entry.id), [entry.id]);
  const [currentTab, setCurrentTab] = useState<DatasetDetailTab>(initialTab);
  // 에피소드 탭의 필터. 창을 열 때의 값으로 시작한다. 제목의 채널 누락 배지가 바꾼다.
  const [currentEpisodeFilters, setCurrentEpisodeFilters] = useState(episodeFilters);
  // 가드가 보류한 이탈. 나가기를 고르면 이 동작을 그대로 실행한다. null 이면 모달이 닫혀 있다.
  const [pendingExit, setPendingExit] = useState<DatasetDetailExitIntent | null>(null);

  // 이미 열린 창을 다른 탭으로 다시 열면(예: Dataset 목록에서 연 창을 리뷰 큐에서 클릭) params
  // 의 initialTab 이 바뀐다. 그래서 그 탭으로 옮긴다.
  useEffect(() => {
    setCurrentTab(initialTab);
  }, [initialTab]);

  // 이미 열린 창을 새 필터로 다시 열면(예: 목록의 배지) params 의 필터가 바뀐다. 필터는 에피소드 탭에
  // 걸리므로 여는 쪽이 고른 탭으로도 옮긴다. 배지는 기본 탭과 같은 값(episodeReview)으로 연다. 그래서
  // 위 effect 는 돌지 않는다. 사용자가 다른 탭에 있었어도 여기서 옮긴다.
  useEffect(() => {
    setCurrentEpisodeFilters(episodeFilters);
    if (episodeFilters !== undefined) {
      setCurrentTab(initialTab);
    }
  }, [episodeFilters, initialTab]);

  // 확정·되돌리기는 이 데이터셋의 검토를 끝내는 동작이다. 그래서 창을 닫는다(여는 쪽이 큐를
  // 새로고침한다). 검토 요청·저장(direct) 뒤에는 같은 창에서 계속 볼 수 있다. 그래서 창을 유지한다.
  const handleReviewOutcome = useCallback(
    (outcome: DatasetReviewOutcome) => {
      if (outcome === "confirmed" || outcome === "reverted") {
        closeSelf();
      }
    },
    [closeSelf],
  );

  const controller = useDatasetDetailController(datasetUuid, {
    reviewEnabled: true,
    reviewMode: mode,
    annotationEnabled: currentTab === "annotation",
    onRequestTab: setCurrentTab,
    onReviewOutcome: handleReviewOutcome,
  });

  const dataset = controller.currentDataset;
  const title = dataset?.display_name ?? entry.title;
  // 리뷰 draft 는 컨트롤러 메모리에만 있다. 그래서 창을 닫으면 사라진다. 최소화해도 컨트롤러는
  // 남는다. 그래서 가드하지 않는다. 저장 중(busy)에는 종료를 막는다.
  const datasetReviewDirty = controller.review.snapshot.dirty;
  const datasetReviewBusy = controller.review.snapshot.busy;
  // 에피소드 검토 draft 는 리뷰 독의 로컬 상태다. 창을 닫으면 사라진다. 최소화도 독을 unmount 한다.
  // 그래서 최소화에서도 사라진다. 최소화에는 가드가 없다.
  // 편집은 리뷰 독이 가지고 닫기 버튼은 이 셸이 가진다. 둘은 형제다. 그래서 스토어로 연결한다.
  // 저장이 끝나면 dirty 가 해제되고 가드도 함께 해제된다.
  const episodeReviewDirty = useEpisodeReviewDirty(datasetUuid);
  // 어노테이션 draft 는 탭을 전환해도 유지된다(컨트롤러 상주). 창을 닫을 때만 사라진다. 그래서
  // 탭 이탈 가드에는 포함하지 않고 닫기 가드에만 포함한다.
  const annotationDirty = controller.annotationStudio.snapshot.isDirty;
  const hasUnsavedEdits = datasetReviewDirty || episodeReviewDirty || annotationDirty;
  const hasReviewEdits = datasetReviewDirty || episodeReviewDirty;

  // 데이터를 적재한 뒤 dock 라벨(제목)을 보정한다(딥링크로 이름 없이 연 경우 포함).
  useEffect(() => {
    if (dataset) {
      windowStack.update(entry.id, {
        title: dataset.display_name,
        label: windowTaskbarTextLabel(
          mode === "candidate" ? "리뷰:" : "공용:",
          dataset.display_name,
        ),
      });
    }
  }, [entry.id, dataset, mode]);

  // 새로고침·탭 닫기로도 draft 가 사라진다. 그래서 dirty 인 동안만 네이티브 경고를 등록한다.
  useEffect(() => {
    if (!hasUnsavedEdits) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedEdits]);

  const requestAnnotationClose = (): void => {
    void controller.annotationStudio.controller.requestLeave("close").then((allowed) => {
      if (allowed) closeSelf();
    });
  };

  const handleAttemptClose = (): void => {
    if (datasetReviewBusy) {
      return;
    }
    if (hasReviewEdits) {
      setPendingExit({ kind: "close" });
      return;
    }
    requestAnnotationClose();
  };

  // 모드 전환은 초안을 보존한다. 저장 중에는 이동하지 않는다. 옮겼으면 true 를 반환한다.
  const handleTabRequest = (tab: DatasetDetailTab): boolean => {
    if (datasetReviewBusy) {
      return false;
    }
    setCurrentTab(tab);
    return true;
  };

  // 제목의 채널 누락 배지. 탭 이동은 handleTabRequest 를 거친다. 옮기지 못했으면 필터도 두지 않는다.
  const openMissingChannelEpisodes = (): void => {
    if (handleTabRequest("episodeReview")) {
      setCurrentEpisodeFilters(missingChannelEpisodeFilters());
    }
  };

  const exitGuardTargets = (): DatasetDetailExitGuardTargets => ({
    datasetReviewDirty,
    resetDatasetReview: () => controller.review.controller.resetDraft(),
    episodeReview: episodeReviewDirtyStore.get(datasetUuid),
  });

  // 이 가드는 저장하지 않는다. 머무르면 편집을 그대로 두고 사용자를 저장 버튼 앞으로 돌려보낸다.
  const stay = (): void => setPendingExit(null);

  // 편집을 버리고 보류한 이탈을 실행한다.
  const discardAndExit = (): void => {
    if (pendingExit === null) {
      return;
    }
    discardGuardedEdits(exitGuardTargets());
    setPendingExit(null);
    if (pendingExit.kind === "close") {
      requestAnnotationClose();
      return;
    }
    setCurrentTab(pendingExit.tab);
  };

  const stepHeader = (
    <div className={styles.tabs} role="tablist" aria-label="Dataset 상세 섹션">
      {datasetDetailTabs.map((tab) => (
        <IconButton
          key={tab.value}
          text={tab.label}
          variant="surface"
          selected={tab.value === datasetDetailModeTab(currentTab)}
          onClick={() => handleTabRequest(tab.value)}
        />
      ))}
    </div>
  );

  // 스트립 양옆의 [←] [→] 버튼과 ←/→ 키. 이동은 반드시 handleTabRequest 를 거친다. 탭을 옮기는
  // 모든 경로가 같은 이탈 가드·저장 중 잠금을 거친다. 그래서 키로 옮길 때만 draft 가 사라지는
  // 문제가 없다.
  const prevTab = adjacentDatasetDetailTab(currentTab, -1);
  const nextTab = adjacentDatasetDetailTab(currentTab, 1);
  const stepNav = {
    onPrev: () => prevTab !== null && handleTabRequest(prevTab),
    onNext: () => nextTab !== null && handleTabRequest(nextTab),
    prevDisabled: prevTab === null,
    nextDisabled: nextTab === null,
    prevLabel: stepNavLabel(prevTab, "이전"),
    nextLabel: stepNavLabel(nextTab, "다음"),
  };

  // 에피소드·리뷰·어노테이션 탭은 본문이 영역을 채우고 내부에서만 스크롤한다. 상세 탭은 콘텐츠가
  // 길면 이 영역이 스크롤된다. 스크롤은 c6 Scrollbar(themed thumb)로 그린다.
  const fills = true;

  return (
    <>
      <FullscreenWindow
        open={active}
        title={title}
        stepHeader={stepHeader}
        stepNav={stepNav}
        titleBadge={datasetDetailTitleBadge(controller, openMissingChannelEpisodes)}
        headerActions={<DatasetDetailActions controller={controller} />}
        // 스크롤 소유자는 탭마다 다르다. fill 탭은 본문이, 나머지는 c6 Scrollbar 가 소유한다.
        bodyLayout="fill"
        onMinimize={() => windowStack.minimize(entry.id)}
        onBackdrop={() => windowStack.minimize(entry.id)}
        onRequestClose={handleAttemptClose}
      >
        {fills ? (
          <div className={styles.body} data-fill="true">
            <DatasetDetailBody
              controller={controller}
              activeTab={currentTab}
              annotationSetUuid={annotationSetUuid}
              episodeFilters={currentEpisodeFilters}
            />
          </div>
        ) : (
          <Scrollbar className={styles.body} aria-label="Dataset 상세 내용">
            <DatasetDetailBody
              controller={controller}
              activeTab={currentTab}
              annotationSetUuid={annotationSetUuid}
              episodeFilters={currentEpisodeFilters}
            />
          </Scrollbar>
        )}
      </FullscreenWindow>
      <DatasetDetailModals controller={controller} />
      {/* 이탈 가드. 설정·검토 draft 는 메모리에만 있다. 그래서 창을 닫으면 사라진다
          (최소화는 보존). 선택지는 둘뿐이다. 머무르거나(X·Esc 포함) 편집을 버리고 나간다.
          이 가드는 저장하지 않는다. 무엇을 저장하는지는 그 표면의 저장 버튼이 표시한다. */}
      <HatTextModal
        open={pendingExit !== null}
        label="저장하지 않은 편집이 있습니다."
        caption={unsavedEditsCaption(
          { datasetReviewDirty, episodeReviewDirty },
          pendingExit ?? { kind: "close" },
        )}
        error
        okText="저장하지 않고 나가기"
        cancelText="머무르기"
        busy={datasetReviewBusy}
        onOkPress={discardAndExit}
        onCancelPress={stay}
      />
    </>
  );
}

// 이동 버튼의 접근성 이름 겸 툴팁. 이동할 탭 이름까지 표시한다. 끝 탭이면 이동할 탭이 없다.
// 그래서 방향만 표시하고 버튼은 disabled 로 둔다.
function stepNavLabel(tab: DatasetDetailTab | null, direction: string): string {
  const label = datasetDetailTabs.find((entry) => entry.value === tab)?.label;
  return label === undefined ? `${direction} 섹션이 없습니다.` : `${direction} 섹션: ${label}`;
}
